import { graphql } from 'react-relay';

import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';

import type {
	useIsSmartFoldersEnabledQuery$data,
	useIsSmartFoldersEnabledQuery$variables,
} from './__generated__/useIsSmartFoldersEnabledQuery.graphql.ts';
import { useIsSmartFoldersSupported } from './useIsSmartFoldersSupported.ts';

type UseIsSmartFoldersEnabledResult = {
	isSmartFoldersEnabled: boolean;
	loading: boolean;
};

/** @schema cc */
const query = graphql`
	query useIsSmartFoldersEnabledQuery($spaceKey: String!) {
		space(key: $spaceKey) {
			id
			spaceTypeSettings {
				enabledContentTypes {
					isFoldersEnabled
					isSmartFoldersEnabled
				}
			}
		}
	}
`;

/**
 * Hook to check whether smart folders are fully enabled for a given space.
 *
 * This checks:
 * 1. Smart folders are supported (Rovo enabled + experiment gates) via `useIsSmartFoldersSupported`
 * 2. The `isFoldersEnabled` space type setting is enabled for the given space
 * 3. The `isSmartFoldersEnabled` space type setting is enabled for the given space
 *
 * The GraphQL query is skipped when `useIsSmartFoldersSupported` returns false
 * or when `spaceKey` is empty (e.g. during initial load before the route resolves),
 * avoiding unnecessary or meaningless network requests.
 *
 * @param spaceKey - The space key to check whether folders are enabled for
 */
export const useIsSmartFoldersEnabled = (spaceKey: string): UseIsSmartFoldersEnabledResult => {
	const isSmartFoldersSupported = useIsSmartFoldersSupported();

	const { loading, data } = useQueryNoFurtherUpdateInterop<
		useIsSmartFoldersEnabledQuery$data,
		useIsSmartFoldersEnabledQuery$variables
	>(query, {
		variables: { spaceKey },
		skip: !isSmartFoldersSupported || !spaceKey,
	});

	const enabledContentTypes = data?.space?.spaceTypeSettings?.enabledContentTypes;
	const isFoldersEnabledInSpace = enabledContentTypes?.isFoldersEnabled ?? false;
	const isSmartFoldersEnabledInSpace = enabledContentTypes?.isSmartFoldersEnabled ?? false;

	return {
		isSmartFoldersEnabled:
			isSmartFoldersSupported && isFoldersEnabledInSpace && isSmartFoldersEnabledInSpace,
		loading,
	};
};
